import { useMemo, useState } from 'react';
import { useAuth } from 'core/contexts/auth';
import { useBookingWizard } from 'core/contexts/bookingWizard';
import { useBookingsV2 } from 'core/contexts/bookingV2';
import { useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { PaymentPlatform } from '@utriper/core';
import { Box, Grid2 } from '@mui/material';
import { AlertDialog, PaymentLinkModal } from '@utriper/core-ui';
import { Title } from '../../styles';
import { getPaymentPlatformsFormatted, paymentDialogPlatforms } from './paymentPlatform';
import Summary from '../SelectActivityForm/components/Summary';
import PaymentPlatformCard from './components/PaymentPlatformCard';
import PaymentDialog from './components/PaymentDialog';
import StripeDialog from './components/StripeDialog';

const DIALOG_TYPE = {
    PAYMENT_DIALOG: 0,
    STRIPE_DIALOG: 1,
    LINK_DIALOG: 2,
};

const ConfirmDialog = ({ dialogType, ...props }) => {
    switch (dialogType) {
        case DIALOG_TYPE.PAYMENT_DIALOG:
            return <PaymentDialog {...props} />;
        case DIALOG_TYPE.STRIPE_DIALOG:
            return <StripeDialog {...props} />;
        default:
            break;
    }
};

const PaymentForm = ({ form }) => {
    const { i18n } = useTranslation();
    const { userInfo } = useAuth();
    const { checkoutItems, pricing, contractId, bookingId, overrideAvailability, setBookingId, setIsSuccess } = useBookingWizard();
    const { addBooking, confirmPaymentLink, cancelBooking, cancelPaymentLink, submitting } = useBookingsV2();
    const { values } = useFormState();
    const acceptedPaymentPlatforms = useMemo(() => getPaymentPlatformsFormatted(userInfo?.experiencer?.acceptedPaymentPlatforms || [], i18n.language), [userInfo, i18n.language]);
    const isPaymentPlatformRequired = useMemo(() => pricing?.payTotal > 0, [pricing]);
    const [bottomSpace, setBottomSpace] = useState(0);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [openPaymentLinkDialog, setOpenPaymentLinkDialog] = useState(false);
    const [dialogType, setDialogType] = useState(null);
    const [bookingData, setBookingData] = useState(null);

    const handleIsPendingTour = () => {
        form.change('isPendingTour', true);
        form.change('paymentPlatformId', null);
    };

    const handlePaymentPlatform = (paymentPlatformId) => {
        form.change('isPendingTour', false);
        form.change('paymentPlatformId', paymentPlatformId);
    };

    const handleSubmit = async () => {
        const { firstName, lastName, email, mobilePhone, mobilePhoneCountryCode, paymentPlatformId, isPendingTour, hostAgent, additional } = values;
        const valuesToSend = {
            items: checkoutItems,
            triper: {
                firstName,
                lastName,
                email,
                mobilePhone,
                mobilePhoneCountryCode: mobilePhoneCountryCode || 'us', // TODO: FIX THIS
            },
            contractId,
            paymentPlatformId,
            isPendingTour,
            overrideAvailability,
            hostAgent,
            additional,
        };

        const result = await addBooking(valuesToSend);
        const hasPaymentPlatform = result?.bookingId && paymentPlatformId;

        if (!result?.bookingId) {
            // THROW ERROR?
        }

        if (result?.payTotal > 0) {
            if (hasPaymentPlatform && paymentDialogPlatforms.includes(paymentPlatformId)) {
                setDialogType(DIALOG_TYPE.PAYMENT_DIALOG);
                setOpenConfirmDialog(true);
            }

            if (hasPaymentPlatform && result?.payment?.clientSecret && paymentPlatformId === PaymentPlatform.IDS.stripe) {
                setDialogType(DIALOG_TYPE.STRIPE_DIALOG);
                setOpenConfirmDialog(true);
            }

            if (hasPaymentPlatform && paymentPlatformId === PaymentPlatform.IDS.link) {
                setDialogType(DIALOG_TYPE.LINK_DIALOG);
                setOpenPaymentLinkDialog(true);
            }
        }

        setBookingData(result);
        setBookingId(result?.bookingId);

        if (result?.payTotal === 0) {
            setIsSuccess(true);
        }

        if (!paymentPlatformId && result?.bookingId && isPendingTour) {
            setIsSuccess(true);
        }
    };

    const handleSubmitLink = async (formValues) => {
        if (values?.paymentPlatformId !== PaymentPlatform.IDS.link || !bookingId) {
            return; // TODO: ADD ERROR MESSAGE
        }

        const dataToSend = {
            email: formValues?.email,
            mobilePhone: formValues?.mobilePhone,
            expiryDate: formValues?.expiryDay,
            bookingId,
        };

        const result = await confirmPaymentLink(dataToSend);

        if (result) {
            setIsSuccess(true);
        }
        setOpenPaymentLinkDialog(false);
    };

    const handleCancelStripe = async () => {
        if (values?.paymentPlatformId !== PaymentPlatform.IDS.stripe || !bookingData?.payment?.paymentId) return;

        const paymentId = bookingData.payment.paymentId;

        await cancelBooking(paymentId);
    };

    return (
        <Grid2 size={12} sx={{ height: '100%', display: 'flex', justifyContent: 'space-between', flexDirection: { xs: 'column', md: 'row' } }}>
            {/* nowrap: a Grid2 container wraps by default, and stretched to the step's height a long list wrapped into a second column under the Summary. */}
            <Grid2 container direction="column" wrap="nowrap" size={{ xs: 12, md: 6 }} sx={{ pb: { xs: `${bottomSpace}px`, md: 2 } }}>
                <Title title="¿Cómo pagará el viajero?" description="Selecciona el método de pago con el que se va a cobrar esta reserva." />

                <Box display="flex" flexDirection="column" gap={1.5} width="100%" maxWidth="640px">
                    {/* Is Pending Tour */}
                    <PaymentPlatformCard id={PaymentPlatform.IDS.undefined} name={'Tour a Confirmar'} isSelected={values?.isPendingTour} handlePaymentPlatform={handleIsPendingTour} disabled={!isPaymentPlatformRequired} />

                    {/* Payment Platforms */}
                    {acceptedPaymentPlatforms?.map((paymentPlatform) => (
                        <PaymentPlatformCard key={paymentPlatform?.id} id={paymentPlatform?.id} name={paymentPlatform?.name} isSelected={values?.paymentPlatformId && paymentPlatform?.id === values?.paymentPlatformId} handlePaymentPlatform={handlePaymentPlatform} disabled={!isPaymentPlatformRequired} />
                    ))}
                </Box>
            </Grid2>

            <Grid2 container size={{ xs: 12, md: 6 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Summary setBottomSpace={setBottomSpace} hideBack={false} hideHeader label="Reservar" disabled={!values?.paymentPlatformId && !values?.isPendingTour && isPaymentPlatformRequired} handleSubmit={handleSubmit} loading={submitting} />
            </Grid2>

            <AlertDialog
                open={openConfirmDialog}
                handleClose={() => {
                    setOpenConfirmDialog(false);
                    handleCancelStripe();
                }}
                hideTitle
                hideSubmit
                hideBack={values?.paymentPlatformId !== PaymentPlatform.IDS.stripe}
                labelBack="Cancelar"
                dialogContent={
                    <ConfirmDialog
                        dialogType={dialogType}
                        // PaymentDialog Props
                        paymentPlatformId={values?.paymentPlatformId}
                        bookingData={bookingData}
                        onClose={() => {
                            setOpenConfirmDialog(false);
                            handleCancelStripe();
                        }}
                        // StripeDialog Props
                        open={openConfirmDialog}
                        clientSecret={bookingData?.payment?.clientSecret}
                    />
                }
                maxWidth="sm"
                fullWidth
            />

            <PaymentLinkModal
                open={openPaymentLinkDialog}
                setOpen={setOpenPaymentLinkDialog}
                paymentData={{
                    payTotalPrice: bookingData?.payTotal,
                    payNowPrice: bookingData?.payNow,
                    currency: bookingData?.currency,
                }}
                initialValues={{
                    email: values?.email,
                    mobilePhone: values?.mobilePhone,
                    expiryDay: new Date(new Date().getTime() + 720 * 60000),
                }}
                onClose={() => {
                    cancelPaymentLink(bookingData?.payment?.paymentLink);
                    setOpenPaymentLinkDialog(false);
                }}
                onSubmit={handleSubmitLink}
            />
        </Grid2>
    );
};

export default PaymentForm;
